import React from "react";
import { MatchState, Inning } from "../../matchTypes";
import { getOversDisplay } from "../../engine/MatchEngine";
import TeamLogo from "./TeamLogo";

interface InningsCompleteModalProps {
  state: MatchState;
  firstInning: Inning;
  onStartSecondInnings: () => void;
}

const InningsCompleteModal: React.FC<InningsCompleteModalProps> = ({
  state,
  firstInning,
  onStartSecondInnings,
}) => {
  const battingTeamName =
    firstInning.battingTeam === "A" ? state.teamAName : state.teamBName;
  const battingTeamLogo =
    firstInning.battingTeam === "A" ? state.teamALogo : state.teamBLogo;

  const chasingTeamKey = firstInning.battingTeam === "A" ? "B" : "A";
  const chasingTeamName =
    chasingTeamKey === "A" ? state.teamAName : state.teamBName;
  const chasingTeamLogo =
    chasingTeamKey === "A" ? state.teamALogo : state.teamBLogo;

  const target = firstInning.totalRuns + 1;
  const runsRequired = target;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-2xl border border-emerald-700/30 shadow-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(6,78,59,0.95), rgba(4,47,46,0.97))",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Stadium glow accent */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-32 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(16,185,129,0.15), transparent 70%)",
          }}
        />

        {/* Branding */}
        <div className="pt-7 pb-2 text-center relative">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400/70 font-semibold">
            WatchWicket
          </div>
        </div>

        {/* Title */}
        <div className="text-center px-6 pb-2">
          <h2 className="text-xl font-extrabold text-white tracking-wide uppercase">
            1st Innings Complete
          </h2>
        </div>

        {/* Batting team score card */}
        <div className="px-6 pt-4 pb-3">
          <div className="flex flex-col items-center gap-2 rounded-xl bg-black/25 border border-white/5 p-5">
            <TeamLogo logoUrl={battingTeamLogo} teamName={battingTeamName} size="md" />
            <div className="text-center">
              <div className="text-white font-bold text-lg leading-tight">{battingTeamName}</div>
              <div className="text-3xl font-black text-white mt-1 tracking-tight">
                {firstInning.totalRuns}/{firstInning.wickets}
              </div>
              <div className="text-emerald-300/80 text-sm font-medium mt-0.5">
                {getOversDisplay(firstInning.totalBalls)} overs
              </div>
            </div>
          </div>
        </div>

        {/* Chase info */}
        <div className="px-6 pb-5">
          <div className="flex items-center gap-3 rounded-xl bg-emerald-900/30 border border-emerald-600/20 p-4">
            <TeamLogo logoUrl={chasingTeamLogo} teamName={chasingTeamName} size="sm" />
            <div className="flex-1 min-w-0">
              <div className="text-emerald-300/70 text-[10px] uppercase tracking-wider font-semibold">
                Target
              </div>
              <div className="text-white font-bold text-lg leading-tight truncate">
                {chasingTeamName}
              </div>
              <div className="text-emerald-400 font-bold text-base">
                TARGET {target} &middot; Need {runsRequired} runs to win
              </div>
            </div>
          </div>
        </div>

        {/* Action */}
        <div className="px-6 pb-7">
          <button
            onClick={onStartSecondInnings}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all active:scale-[0.98]"
          >
            Start 2nd Innings
          </button>
        </div>
      </div>
    </div>
  );
};

export default InningsCompleteModal;
